import { create } from 'zustand';
import type { RecordMode } from '@engine/transport';

export type QuantGrid = 1 | 0.5 | 0.25 | 0.125;

interface TransportState {
  /** Tempo in BPM. Drives the metronome + quantize grid. */
  bpm: number;
  /** Beats per bar. Strong click lands on beat 1. */
  quantum: number;

  metronomeOn: boolean;
  /** Master metronome click volume. */
  metronomeVolume: number;

  quantizeOn: boolean;
  /** 0 = no pull (free play), 1 = full snap, in between = humanize. */
  quantizeStrength: number;
  /** Grid division in beats. 0.25 = 16th, etc. */
  quantGrid: QuantGrid;

  /** Current visual beat (integer), updated on each metronome tick.
   *  -1 when metronome is off / hasn't fired yet. */
  visualBeat: number;
  visualBeatAtMs: number;
  visualBeatStrong: boolean;

  recordMode: RecordMode;
  recording: boolean;

  /** Live readout: how much the LAST pluck got pushed by quantize, in
   *  milliseconds. Diagnostic; shown in the Transport panel so the
   *  player can see the snap is actually firing. */
  lastQuantShiftMs: number;
  lastQuantAtMs: number;

  setBpm: (v: number) => void;
  setQuantum: (v: number) => void;
  setMetronomeOn: (v: boolean) => void;
  toggleMetronome: () => void;
  setMetronomeVolume: (v: number) => void;
  setQuantizeOn: (v: boolean) => void;
  toggleQuantize: () => void;
  setQuantizeStrength: (v: number) => void;
  setQuantGrid: (v: QuantGrid) => void;
  setVisualBeat: (beat: number, strong: boolean) => void;
  setRecordMode: (m: RecordMode) => void;
  setRecording: (v: boolean) => void;
  reportQuantShift: (ms: number) => void;
}

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));

export const useTransportStore = create<TransportState>((set) => ({
  bpm: 90,
  quantum: 4,
  // Default ON — user directive: groove-out-of-the-box. 90 BPM with
  // a 1/16 grid is the most playable default for đàn tranh phrasing
  // (gliss subdivision lands evenly within an audible cell).
  metronomeOn: true,
  metronomeVolume: 0.7,
  quantizeOn: true,
  quantizeStrength: 1.0,
  quantGrid: 0.25,
  visualBeat: -1,
  visualBeatAtMs: 0,
  visualBeatStrong: false,
  recordMode: 'full',
  recording: false,
  lastQuantShiftMs: 0,
  lastQuantAtMs: 0,

  setBpm: (v) => set({ bpm: clamp(Math.round(v), 30, 240) }),
  setQuantum: (v) => set({ quantum: clamp(Math.round(v), 1, 12) }),
  setMetronomeOn: (metronomeOn) => set({ metronomeOn }),
  toggleMetronome: () => set((s) => ({ metronomeOn: !s.metronomeOn })),
  setMetronomeVolume: (v) => set({ metronomeVolume: clamp(v, 0, 1) }),
  // Linking: enabling quantize without a click is impossible to hear
  // (the snap has no reference). When quantize turns on we also turn on
  // metronome — the player can mute the click after if they don't want
  // it, but the default makes "khoá" actually audible.
  setQuantizeOn: (quantizeOn) =>
    set((s) => ({
      quantizeOn,
      metronomeOn: quantizeOn ? true : s.metronomeOn,
    })),
  toggleQuantize: () =>
    set((s) => ({
      quantizeOn: !s.quantizeOn,
      metronomeOn: !s.quantizeOn ? true : s.metronomeOn,
    })),
  setQuantizeStrength: (v) => set({ quantizeStrength: clamp(v, 0, 1) }),
  setQuantGrid: (quantGrid) => set({ quantGrid }),
  setVisualBeat: (beat, strong) =>
    set({ visualBeat: beat, visualBeatAtMs: performance.now(), visualBeatStrong: strong }),
  setRecordMode: (recordMode) => set({ recordMode }),
  setRecording: (recording) => set({ recording }),
  reportQuantShift: (ms) =>
    set({ lastQuantShiftMs: ms, lastQuantAtMs: performance.now() }),
}));
